import React from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import NewsletterSignUp from '../../common/Newsletter-sign';
import ImageSlant from '../../common/image-slant';
import heroImage from '../../../assets/christmas25/search.webp';
import grottoImage from '../../../assets/christmas25/grotto.webp';
import teaImage from '../../../assets/christmas25/tea2.webp';
import { useBlackFriday } from '../../../contexts/BlackFridayContext';

const Page = styled.div`
  min-height: 100vh;
  padding-top: 120px;
  background-color: ${props => props.theme.colors.offWhite};
`;

const EventHero = styled.div`
  width: 100%;
  height: 420px;
  background-image: ${props => `linear-gradient(to bottom, rgba(0,0,0,0.25), rgba(0,0,0,0.65)), url(${props.image})`};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  position: relative;

  @media (max-width: 1024px) {
    height: 420px;
  }

  @media (max-width: 768px) {
    height: 340px;
  }
`;

const HeroContent = styled.div`
  width: 100%;
  max-width: 1400px;
  padding: 3rem 2rem;
  color: white;
`;

const Badge = styled.span`
  display: inline-block;
  background: ${props => props.theme.colors.accent};
  color: white;
  padding: 0.4rem 0.8rem;
  border-radius: 999px;
  font-size: 0.95rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
`;

const Title = styled.h1`
  font-family: ${props => props.theme.fonts.main};
  font-size: 3rem;
  font-weight: 700;
  margin: 0.25rem 0 0.5rem;
  line-height: 1.2;
  text-shadow: 0 2px 6px rgba(0,0,0,0.35);
`;

const Strapline = styled.p`
  font-size: 1.25rem;
  margin: 0;
  opacity: 0.95;
`;

const Content = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 1.5rem auto 0;
  padding: 0 2rem 3rem;
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 2.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    margin-top: 1rem;
  }
`;

const Card = styled.section.attrs({ className: 'bf-surface' })`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 10px 30px rgba(0,0,0,0.08);
  z-index: 1;
  position: relative;
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1rem;
  position: relative;
  padding-bottom: 0.5rem;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: 0;
    width: 80px;
    height: 3px;
    background: ${props => props.theme.colors.accent};
  }
`;

const Body = styled.div`
  font-size: 1.1rem;
  line-height: 1.8;
  color: #333;

  p { margin: 0 0 1rem; }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InfoItem = styled.div.attrs({ className: 'bf-surface' })`
  background: ${props => props.theme.colors.offWhite};
  padding: 1rem 1.25rem;
  border-radius: 8px;
`;

const InfoLabel = styled.div`
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.25rem;
`;

const PriceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-top: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PriceCard = styled.div.attrs({ className: 'bf-surface' })`
  background: ${props => props.theme.colors.offWhite};
  border-radius: 8px;
  padding: 1rem 1.25rem;
  text-align: center;
`;

const PriceType = styled.div`
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
`;

const Price = styled.div`
  font-size: 1.8rem;
  font-weight: 700;
  color: ${props => props.theme.colors.accent};
  margin-top: 0.25rem;
`;

const CTAButton = styled.a.attrs({ className: 'cta-button' })`
  display: inline-block;
  background: ${props => props.theme.colors.accent};
  color: white;
  padding: 0.9rem 1.5rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 700;
  margin-top: 1.25rem;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.95;
    transform: translateY(-2px);
  }
`;

const Gallery = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1rem;
  margin-top: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const GalleryImage = styled.div`
  background-image: ${props => `url(${props.src})`};
  background-size: cover;
  background-position: center;
  border-radius: 8px;
  height: 220px;
`;

const SearchForSanta = () => {
  const { isActive: isBlackFriday } = useBlackFriday();
  const pageClassName = isBlackFriday ? 'christmas-page christmas-page--black-friday' : 'christmas-page';
  const meta = {
    title: 'Search for Santa at Belvoir Castle 2025 | Christmas Trail',
    description: 'New for Christmas 2025! Join the exclusive Search for Santa at Belvoir Castle. Early morning Christmas family trail with Santa, snacks & drinks included. Christmas activities in Leicestershire. Book now!',
    canonical: 'https://www.belvoircastle.com/christmas/search-for-santa'
  };

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: 'Search for Santa at Belvoir Castle Christmas 2025',
    description: 'New for Christmas 2025! Join the exclusive early morning Search for Santa at Belvoir Castle. Weekends only, starting at 8:30am before the Castle opens. Enjoy a festive family trail with Christmas snacks and drinks included.',
    image: [
      "https://www.belvoircastle.com/christmas25/search.webp",
      "https://www.belvoircastle.com/christmas25/grotto.webp"
    ],
    startDate: '2025-11-29',
    endDate: '2025-12-31',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    eventStatus: 'https://schema.org/EventScheduled',
    location: {
      '@type': 'Place',
      name: 'Belvoir Castle',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Belvoir Castle',
        addressLocality: 'Grantham',
        addressRegion: 'Leicestershire',
        postalCode: 'NG32 1PE',
        addressCountry: 'GB'
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 52.8931,
        longitude: -0.7792
      }
    },
    organizer: {
      '@type': 'Organization',
      name: 'Belvoir Castle',
      url: 'https://www.belvoircastle.com',
      telephone: '+441476871001',
      email: 'info@belvoircastle.com'
    },
    offers: [
      {
        '@type': 'Offer',
        name: 'General Admission',
        price: 35,
        priceCurrency: 'GBP',
        availability: 'https://schema.org/InStock',
        url: meta.canonical
      },
      {
        '@type': 'Offer',
        name: 'Under 3s',
        price: 5,
        priceCurrency: 'GBP',
        availability: 'https://schema.org/InStock',
        url: meta.canonical
      }
    ],
    url: meta.canonical
  };

  return (
    <Page className={pageClassName}>
      <Helmet>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:image" content={heroImage} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Search for Santa at Belvoir Castle" />
        <meta property="og:site_name" content="Belvoir Castle" />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={heroImage} />
        <meta name="twitter:site" content="@belvoircastle" />
        <meta name="twitter:url" content={meta.canonical} />
        <meta name="twitter:image:alt" content="Search for Santa at Belvoir Castle" />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      <EventHero image={heroImage}>
        <HeroContent>
          <Badge>New for 2025</Badge>
          <Title>Search for Santa</Title>
          <Strapline>An exclusive early morning family trail through the Castle to find Santa</Strapline>
        </HeroContent>
      </EventHero>

      <Content>
        <Card>
          <SectionTitle>About the Experience</SectionTitle>
          <Body>
            <p>Gather the family for an exclusive early morning adventure before the Castle opens to the public. Starting at 8:30am, follow a festive trail through Belvoir Castle's grand rooms, solve cheerful clues, and meet friendly characters as you make your way to Santa. Expect twinkling décor, joyful moments, and magical memories at every turn.</p>
            <p>Your ticket includes warming Christmas snacks and drinks to enjoy along the way. Little ones will receive a special present from Santa — the perfect keepsake from a truly unforgettable day.</p>
            <p>Once you've completed the trail, you're welcome to enjoy the whole day at the Castle. Explore more of our seasonal activities — including the charming Mice Trail — all included with your ticket.</p>
          </Body>

          <Gallery>
            <GalleryImage src={heroImage} />
            <GalleryImage src={grottoImage} />
          </Gallery>
        </Card>

        <Card>
          <SectionTitle>Key Information</SectionTitle>
          <InfoGrid>
            <InfoItem>
              <InfoLabel>Dates</InfoLabel>
              <div>29 November – 31 December 2025 (weekends only)</div>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Start Time</InfoLabel>
              <div>8:30am (exclusive early access)</div>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Location</InfoLabel>
              <div>Belvoir Castle</div>
            </InfoItem>
            <InfoItem>
              <InfoLabel>What's Included</InfoLabel>
              <div>Exclusive early morning festive trail, Christmas snacks and drinks, a present for the child, plus full-day access to explore the Castle and the Mice Trail</div>
            </InfoItem>

          </InfoGrid>

          <SectionTitle style={{ marginTop: '1.5rem' }}>Ticket Prices</SectionTitle>
          <PriceGrid>
            <PriceCard>
              <PriceType>General Admission</PriceType>
              <Price>£35</Price>
            </PriceCard>
            <PriceCard>
              <PriceType>Under 3s</PriceType>
              <Price>£5</Price>
            </PriceCard>

          </PriceGrid>

          <CTAButton href="https://online1.venpos.net/VenposJourneyUI/?LocationId=978&Id=d771a85f-632e-4a25-8651-c3a60fb31a10">Book Search for Santa</CTAButton>
        </Card>
      </Content>

      <ImageSlant 
        image={teaImage}
        title="Festive Afternoon Tea"
        description="Make your day even more memorable with indulgent winter treats in our elegant tearooms."
        buttonText="Book Festive Tea"
        buttonAction={() => window.location.assign('/christmas/festive-afternoon-tea')}
        imagePosition="left"
        darkMode={isBlackFriday}
      />

      <div style={{ margin: '3rem 0' }}>
        <NewsletterSignUp tag="Christmas 2025" />
      </div>
    </Page>
  );
};

export default SearchForSanta;

